// =============================================================================
// Strut Multiline — Fix baseline struts for multiline headings
// =============================================================================
// The CSS strut technique (inline-flex + height + ::before) works perfectly
// for single-line text but breaks for multiline. This module detects
// multiline headings and applies a block-based fix.

const STRUT_SELECTOR = "h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4";

/**
 * Check if an element's text wraps to multiple lines
 */
function isMultiline(element: HTMLElement): boolean {
    // Get the computed strut height
    const style = getComputedStyle(element);
    const strutHeight = parseFloat(style.height);

    // Clone to measure natural content height
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
    position: absolute;
    visibility: hidden;
    display: block;
    height: auto;
    width: ${element.offsetWidth}px;
    white-space: normal;
  `;
    document.body.appendChild(clone);
    const contentHeight = clone.scrollHeight;
    document.body.removeChild(clone);

    // If content height exceeds 1.5x strut height, it's multiline
    return contentHeight > strutHeight * 1.5;
}

/**
 * Apply multiline fix — keep strut line-height, just allow wrapping
 */
function applyMultilineFix(element: HTMLElement): void {
    element.style.display = "inline-flex";
    element.style.flexWrap = "wrap";
    element.style.height = "auto";
    element.classList.add("strut--multiline");
}

/**
 * Remove multiline fix (if element becomes single-line again)
 */
function removeMultilineFix(element: HTMLElement): void {
    element.style.display = "";
    element.style.flexWrap = "";
    element.style.height = "";
    element.classList.remove("strut--multiline");
}

/**
 * Check and fix all strut elements
 */
function checkAllStruts(): void {
    const elements = document.querySelectorAll<HTMLElement>(STRUT_SELECTOR);

    elements.forEach((element) => {
        // Temporarily remove fix to check true state
        if (element.classList.contains("strut--multiline")) {
            removeMultilineFix(element);
        }

        if (isMultiline(element)) {
            applyMultilineFix(element);
        }
    });
}

/**
 * Initialize strut multiline detection
 */
export function initStrutMultiline(): void {
    // Check after fonts are loaded
    if (document.fonts?.ready) {
        document.fonts.ready.then(checkAllStruts);
    } else {
        setTimeout(checkAllStruts, 100);
    }

    // Re-check on resize (text may reflow)
    let resizeTimeout: number;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(checkAllStruts, 150);
    });
}
